<template>
  <div id="app">
    <transition name="component-fade" mode="out-in">
      <router-view />
    </transition>
    <SurveyPrompt />
    <MobileMenuSidebar />
    <prompt-modal :parameters="promptModalData" />
    <AgeGatingModal/>
    <Drawer />
    <SsoAccountLinkedModal />
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from "vuex";
import SurveyPrompt from "@/components/customer/survey-prompt-modal";
import MobileMenuSidebar from "@/components/layout/MobileSidebar";
import PromptModal from '@/components/PromptModal.vue';
import "bootstrap-icons/font/bootstrap-icons.css";
import Vue from "vue";
import VueGtm from '@gtm-support/vue2-gtm';
import router from "./router";
import { hexToRgba } from "@/utilities";
import { mapFields } from "vuex-map-fields";
import AgeGatingModal from "./components/common/AgeGatingModal.vue";
import Drawer from "@/components/common/Drawer.vue";
import SsoAccountLinkedModal from "@/components/authentication/SsoAccountLinkedModal.vue";

export default {
  components: {
    SurveyPrompt,
    MobileMenuSidebar,
    PromptModal,
    AgeGatingModal,
    Drawer,
    SsoAccountLinkedModal
  },
  data() {
    return {
      timerCount: 3600,
      promptModalData: {
        title: "Oops!",
        message: "",
        buttonText: "OK",
      },
      gtmId: "G-JYY42FLJMF"
    };
  },
  watch: {
    isIdle: function () {
      if (this.isIdle && this.loggedIn) {
        this.$store.dispatch("auth/logout");
      }
    },
    timerCount: {
      handler(value) {
        if (value > 0) {
          setTimeout(() => {
            this.timerCount--;
          }, 1000);
        } else {
          if (this.loggedIn) {
            this.$store.dispatch("auth/logout");
          }
        }
      },
      immediate: true,
    },
    maintenance: function () {
      if (this.maintenance && !this.$route.path.includes("maintenance")) {
        this.$router.push(`/${this.$route.params.subdomain}/maintenance`);
      }
    },
    subdomain(subdomain) { // transferred from watch: $route(to) if (to.name != "404")
      if (subdomain && this.$route.name !== "404") {
        this.$store.dispatch("merchant/fetchSubdomain", subdomain).then(() => {
          this.$store.dispatch("merchant/fetchCustomerPortalToken").then(() => {
            this.$store.dispatch("merchant/fetchMerchantSettings");
            this.$store.dispatch("merchant/fetchMerchantMaintenance");

            if (!this.iconsAndLogos) this.initializeFavicon();
          });
          this.$nextTick(() => {
            if (this.subdomain === "ucoin") {
              document.title = "U-COIN | Unilever's Reward Program";

              if (this.loggedIn) {
                this.requestSurveyStatus();
              }
            }
          });
        });
      }
    },
    async customerPortalToken(token) {
      if (token) {
        this.getWebDesign();
        this.getStoreDetails();
        this.getHomeLayout();
        await this.$store.dispatch("merchant/getGoogleTagCode");
        if (this.gtmData.is_enable) {
          Vue.use(VueGtm, {
            id: this.gtmData.code,
            enable: this.gtmData.is_enable,
            loadScript: this.gtmData.is_enable,
            vueRouter: router
          });
        }
      } 
    },
    merchantSettings(settings) {
      if(!this.loggedIn && (this.$route.name == 'PaymentSuccess' || this.$route.name == 'PaymentFailed')) {
        sessionStorage.setItem('confirmAge', '1')
      }
      if(settings && Object.hasOwn(settings, 'enable_age_gating')) {
        if(!this.loggedIn) {
          if(settings.enable_age_gating && sessionStorage.getItem('confirmAge') !== '1') {
            this.$root.$emit("bv::show::modal", "age-gating-modal");
          }
        }
      }
    },
    loggedIn(isLoggedIn) {
      if (!isLoggedIn && this.merchantSettings?.enable_age_gating && sessionStorage.getItem('confirmAge') !== '1') {
        this.$root.$emit("bv::show::modal", "age-gating-modal");
      }
    }
  },
  computed: {
    ...mapState("merchant", {
      customerPortalToken: (state) => state.customerPortalToken,
      maintenance: (state) => state.maintenance,
      gtmData: (state) => state.gtmData,
      merchantSettings: state => state.merchantSettings
    }),
    ...mapState("auth", {
      token: (state) => state.token,
    }),
    ...mapState("design", ["iconsAndLogos"]),
    isIdle() {
      return this.$store.state.idleVue.isIdle;
    },
    ...mapGetters({
      loggedIn: "auth/loggedIn",
      surveyStatus: "customer/surveyStatus",
      homeWebLayout: "design/homeWebLayout",
      design: "estore/design"
    }),
    subdomain: {
      get() {
        return this.$route.params.subdomain;
      },
      set(subdomain) {
        return subdomain;
      }
    },
    ...mapFields("isMobile", ["isMobile"])
  },
  methods: {
    ...mapActions({
      getSurveyStatus: "customer/getSurveyStatus",
      getHomeWebLayout: "design/getHomeWebLayout",
    }),
    getHomeLayout() {
      const request = {
        header: {
          "X-Merchant-Token": this.customerPortalToken
        }
      };

      this.getHomeWebLayout(request);
    },
    async requestSurveyStatus() {
      const request = {
        header: {
          Authorization: this.token,
          "X-Merchant-Token": this.customerPortalToken,
        },
      };
      await this.getSurveyStatus(request).then(() => {
        if (!this.surveyStatus) {
          this.$root.$emit("openSurveyPromptModal");
        }
      });
    },
    async initializeFavicon() {
      await this.$store.dispatch("design/getIconsAndLogos", {
        header: {
          "X-Merchant-Token": this.customerPortalToken,
        },
      });

      if (this.iconsAndLogos && this.iconsAndLogos.favicon)
        document.getElementById("favicon").href = this.iconsAndLogos.favicon;
    },
    async getWebDesign() {
      let request = {
        header: {
          "X-Merchant-Token": this.customerPortalToken
        }
      };

      await this.$store.dispatch("estore/getWebDesign", request);

      if (Object.keys(this.design).length) {
        document.documentElement.style.setProperty('--main-color', this.design.main_color);
        document.documentElement.style.setProperty('--main-color-50', hexToRgba(this.design.main_color, 0.5));
        document.documentElement.style.setProperty('--main-color-10', hexToRgba(this.design.main_color, 0.1));
        document.documentElement.style.setProperty('--button-text-color', this.design.button_text_color);
      }       
    },
    getStoreDetails() {
      const header = {
        "X-Merchant-Token": this.customerPortalToken
      };

      if (this.token) header["Authorization"] = this.token;

      const request = { header };
      
      this.$store.dispatch("estore/getStoreDetails", request);
    },
    initializeGTM() {
      const gtmScript = document.createElement('script');
      gtmScript.src = `https://www.googletagmanager.com/gtag/js?id=${this.gtmId}`;
      gtmScript.async = true;
      gtmScript.defer = true;
      document.head.appendChild(gtmScript);
      
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }
      gtag('js', new Date());
      gtag('config', this.gtmId);
    }
  },
  metaInfo() {
    return {
      title: this.homeWebLayout["snippet_details.title"]
        ? this.homeWebLayout["snippet_details.title"]
        : "RUSH E-Store",
      meta: [
        {
          name: "description",
          content: this.homeWebLayout["snippet_details.description"] ? this.homeWebLayout["snippet_details.description"] : 'RUSH E-Store' ,
        },
      ],
    };
  },
  created() {
    this.isMobile = window.innerWidth < 845;
  },
  mounted() {
    if(sessionStorage.getItem("confirmAge") === '1') {
      sessionStorage.removeItem("confirmAge");
    }
    sessionStorage.removeItem("isUploadReceipt");
    this.$root.$on("showPromptModal", (parameters) => {
      this.promptModalData = { ...this.promptModalData, ...parameters };
      this.$root.$emit("bv::show::modal", "prompt-modal");
    });
    window.addEventListener("resize", () => {
      this.isMobile = window.innerWidth < 845;
    });
    window.addEventListener('storage', (event) => {
      const isTokenRemovedFromAnotherTab = event.key === 'token' && !event.newValue;

      if (isTokenRemovedFromAnotherTab) {
        this.$router.push(`/${this.$route.params.subdomain}`);
        location.reload();
      }
    });
    
    if (process.env.VUE_APP_MODE === 'Production') {
      this.initializeGTM();
    }
  },
};
</script>
